<template>
  <div ref="selectTarget">
    <label v-if="label" class="block text-sm font-medium leading-6 text-gray-900">{{
      label
    }}</label>

    <div class="relative" :class="{ 'z-50': dropdownActive }">
      <button
        type="button"
        @click="dropdownActive ? closeDropdown() : openDropdown()"
        @mouseenter="showTooltip"
        @mouseleave="hideTooltip"
        ref="buttonRef"
        :class="buttonClass"
        aria-haspopup="listbox"
        :aria-expanded="dropdownActive ? 'true' : 'false'"
        :disabled="disable">
        <span class="block truncate overflow-hidden text-ellipsis whitespace-nowrap"
          >{{ selectedItem?.name || '-' }}
        </span>

        <span v-if="mode == 'edit'" class="absolute inset-y-0 right-0 flex items-center pr-2">
          <IconXmark
            v-if="clearable && selectedItem && !disable"
            :class="size === 'sm' ? 'w-4 h-4' : ' w-5 h-5'"
            class="text-gray-400 transition-colors duration-200 ease-in-out group-hover:text-theme-700"
            @click.stop="selectItem(null)" />
          <IconChevronUpDown
            v-else
            class="w-5 h-5 text-gray-400 transition-colors duration-200 ease-in-out group-hover:text-theme-700" />
        </span>
      </button>
      <Teleport to="body">
        <Transition
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0">
          <ul
            v-if="dropdownActive && mode == 'edit'"
            ref="dropdownRef"
            :style="{
              position: strategy,
              top: y ? `${y}px` : '',
              left: x ? `${x}px` : '',
              zIndex: 9999,
            }"
            class="py-1 mt-1 overflow-auto bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-sm"
            tabindex="-1"
            role="listbox"
            :aria-activedescendant="'listbox-option-' + selected?.value">
            <li
              v-for="item in items"
              :key="item.value"
              :id="'listbox-option-' + item.value"
              @click="selectItem(item)"
              class="relative py-2 pl-3 text-gray-900 transition-colors duration-200 ease-in-out cursor-default select-none hover:text-white pr-9 hover:bg-theme-600 group">
              <span
                :class="item.value === selected?.value ? 'font-semibold' : 'font-normal'"
                class="block whitespace-pre-line"
                >{{ item.displayName || item.name }}</span
              >
              <span
                v-if="item.value === selected?.value"
                class="absolute inset-y-0 right-0 flex items-center pr-4 text-theme-600 group-hover:text-white">
                <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                    clip-rule="evenodd" />
                </svg>
              </span>
            </li>
            <li
              v-if="!items.length"
              class="relative py-2 pl-3 text-gray-900 transition-colors duration-200 ease-in-out cursor-default select-none pr-9 group">
              <slot name="empty-list"> No items found</slot>
            </li>
          </ul>
        </Transition>
        <div
          v-if="!!showErrorAsTooltip && tooltipVisible && error"
          ref="tooltipRef"
          :style="{
            position: tStrategy,
            top: ty ? `${ty}px` : '',
            left: tx ? `${tx}px` : '',
            zIndex: 9999,
          }"
          class="relative flex flex-col items-center px-3 py-2 text-white bg-red-500 rounded-lg shadow-lg">
          <span class="text-xs truncate">{{ error }}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="absolute w-4 h-4 text-red-500 fill-current"
            :style="{ bottom: '-8px', left: 'calc(50% - 8px)', transform: 'rotate(45deg)' }"
            viewBox="0 0 24 24">
            <path
              d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"></path>
          </svg>
        </div>
        <div
          v-if="!!showErrorAsTooltip && tooltipVisible && !error && warning"
          ref="tooltipRef"
          :style="{
            position: tStrategy,
            top: ty ? `${ty}px` : '',
            left: tx ? `${tx}px` : '',
            zIndex: 9999,
          }"
          class="relative flex flex-col items-center px-3 py-2 text-white bg-orange-500 rounded-lg shadow-lg">
          <span class="text-xs truncate">{{ warning }}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="absolute w-4 h-4 text-orange-500 fill-current"
            :style="{ bottom: '-8px', left: 'calc(50% - 8px)', transform: 'rotate(45deg)' }"
            viewBox="0 0 24 24">
            <path
              d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"></path>
          </svg>
        </div>
      </Teleport>
    </div>
    <p v-if="error && !showErrorAsTooltip" class="max-w-48 mx-2 -mt-0.5 text-xs text-red-500">
      {{ error }}
    </p>
    <p
      v-if="!error && warning && !showErrorAsTooltip"
      class="max-w-48 mx-2 -mt-0.5 text-xs text-orange-500">
      {{ warning }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { cva } from 'class-variance-authority';
import { useFloating, offset, flip, shift, autoUpdate, hide } from '@floating-ui/vue';
import IconChevronUpDown from '@/assets/icons/IconChevronUpDown.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import type { SelectDropdownItem } from '@/types/Base';

const props = withDefaults(
  defineProps<{
    label?: string;
    name: string;
    items: SelectDropdownItem[];
    selectedItem: SelectDropdownItem | null;
    flat?: boolean;
    mode?: 'view' | 'edit';
    size?: 'default' | 'sm';
    disable?: boolean;
    error?: string;
    warning?: string;
    showErrorAsTooltip?: boolean;
    clearable?: boolean;
  }>(),
  {
    flat: false,
    mode: 'edit',
    size: 'default',
    showErrorAsTooltip: false,
  }
);

const buttonRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const tooltipVisible = ref(false);

const {
  x: tx,
  y: ty,
  strategy: tStrategy,
  update: tUpdate,
  middlewareData: tMiddelWareData,
} = useFloating(buttonRef, tooltipRef, {
  placement: 'top',
  middleware: [offset(5), flip(), shift(), hide()],
  whileElementsMounted: (referenceEl, floatingEl, tUpdate) => {
    return autoUpdate(referenceEl, floatingEl, tUpdate);
  },
});

function showTooltip() {
  tooltipVisible.value = true;
  tUpdate();
}

function hideTooltip() {
  tooltipVisible.value = false;
}

watchEffect(() => {
  if (tMiddelWareData.value.hide?.referenceHidden) {
    tooltipVisible.value = false;
  }
});

const emit = defineEmits(['update:selected']);

const dropdownActive = ref(false);
const selected = ref<SelectDropdownItem | null>(null);

const dropdownRef = ref<HTMLElement | null>(null);

const { x, y, strategy, middlewareData, update } = useFloating(buttonRef, dropdownRef, {
  placement: 'bottom-start',
  middleware: [offset(5), flip(), shift(), hide()],
  whileElementsMounted: (referenceEl, floatingEl, update) => {
    return autoUpdate(referenceEl, floatingEl, update);
  },
});

watchEffect(() => {
  if (middlewareData.value.hide?.referenceHidden) {
    closeDropdown();
  }
});

onClickOutside(buttonRef, () => closeDropdown());
onClickOutside(dropdownRef, () => closeDropdown());

function openDropdown() {
  dropdownActive.value = true;
  update();
}

function closeDropdown() {
  dropdownActive.value = false;
}

function selectItem(item: SelectDropdownItem | null) {
  selected.value = item;
  closeDropdown();
  emit('update:selected', item);
}

const buttonClass = computed(() => {
  return cva('relative w-full rounded-md pr-10 text-left text-sm leading-6 group pl-3', {
    variants: {
      mode: {
        edit: 'ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-theme-500 text-gray-900',
        view: 'ring-1 ring-inset ring-gray-300 cursor-default bg-gray-50',
      },
      flat: {
        true: '',
        false: 'shadow-sm',
      },
      size: {
        default: 'py-2.5 h-10',
        sm: 'py-1 h-8',
      },
      error: {
        true: 'ring-red-500',
        false: 'ring-gray-300',
      },
      warning: {
        true: 'ring-orange-500',
        false: 'ring-gray-300',
      },
      disabled: {
        true: 'bg-gray-100 text-gray-500 cursor-default !bg-opacity-100 pointer-events-none',
        false: '',
      },
    },
    compoundVariants: [
      {
        mode: 'edit',
        disabled: false,
        class: 'bg-white cursor-pointer',
      },
    ],
  })({
    mode: props.mode,
    flat: props.flat,
    size: props.size,
    error: !!props.error,
    warning: !props.error && !!props.warning,
  });
});

watch(
  () => props.selectedItem,
  (newSelectedItem) => {
    selected.value = newSelectedItem || null;
  },
  { immediate: true }
);
</script>
